import PriceDisplay from '@/components/PriceDisplay';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Typography } from '@/components/primitives/Typography';
import PassengerOptions from '@/components/purchase/checkout/ancillaries/ancillary-modal/PassengerOptions';
import ToggleableRow from '@/components/purchase/checkout/ToggleableRow';
import { useAvailableAncillaries } from '@/hooks/useAvailableAncillaries';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { type LegDTO } from '@/types/dto/agent';
import type { AdditionalOfferItem } from '@/types/offer';
import type { AncillaryValues, CheckoutValues } from '@/utils/zodSchema';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

interface AncillaryOptionsProps {
  selectedLeg?: LegDTO;
  passengers: CheckoutValues['passengers'];
}

const AncillaryOptions: FC<AncillaryOptionsProps> = ({
  selectedLeg,
  passengers,
}) => {
  const { t } = useTranslation();

  const { watch, setValue } = useFormContext<AncillaryValues>();
  const selectedAncillaries = watch('selectedAncillaries');
  const availableAncillaries = useAvailableAncillaries(selectedLeg?.id);

  const handleAncillaryToggle = useCallback(
    (ancillary: AdditionalOfferItem, checked: boolean) => {
      if (!ancillary.id) {
        return;
      }

      if (checked) {
        setValue(`selectedAncillaries`, [
          ...selectedAncillaries,
          {
            id: ancillary.id,
            additionalOfferId: ancillary.additionalOfferId,
            bookedOfferId: ancillary.bookedOfferId,
            amount: ancillary.price?.amount || 0,
            passengersExternalReferences: passengers.map(
              ({ externalReference }) => externalReference
            ),
          },
        ]);
      } else {
        const filteredAncillaries = selectedAncillaries.filter(
          (selected) => selected?.id !== ancillary.id
        );

        setValue('selectedAncillaries', filteredAncillaries);
      }
    },
    [passengers, selectedAncillaries, setValue]
  );

  return (
    <div className="flex flex-col gap-2" data-testid="ancillary-options-list">
      {!!availableAncillaries.length &&
        availableAncillaries.map((ancillary, index) => {
          const isActive = selectedAncillaries.some(
            (selectedAncillary) => selectedAncillary?.id === ancillary.id
          );

          return (
            <div
              className="flex flex-col gap-2"
              key={ancillary.id}
              data-testid={`ancillary-option-${index}`}
            >
              <ToggleableRow
                isActive={isActive}
                onClick={() => handleAncillaryToggle(ancillary, !isActive)}
              >
                <div className="flex w-full items-center gap-2">
                  <Checkbox
                    checked={isActive}
                    className="h-4 w-4 flex-shrink-0"
                    aria-label={t('text.selectAncillary')}
                    onCheckedChange={(checked) =>
                      handleAncillaryToggle(ancillary, checked === true)
                    }
                    data-testid={`ancillary-option-${index}-toggle`}
                  />
                  <div className="flex w-full justify-between">
                    <Typography variant="body1-bold">
                      {ancillary.products?.[0]?.description}
                    </Typography>
                    <Typography variant="body2-bold" className="text-primary">
                      {ancillary.price?.amount === 0 ? (
                        <TransText i18nKey="free" />
                      ) : (
                        <PriceDisplay amount={ancillary.price?.amount} />
                      )}
                    </Typography>
                  </div>
                </div>
              </ToggleableRow>
              {passengers.length > 1 && isActive ? (
                <PassengerOptions
                  ancillaryId={ancillary.id}
                  passengers={passengers}
                />
              ) : null}
            </div>
          );
        })}
    </div>
  );
};

export default AncillaryOptions;
